﻿import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  FileText, CheckCircle2, Clock, AlertTriangle, Download, Loader2,
} from 'lucide-react';
import type { ApiError, MySopItem, ListMySopQuery } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { sopService } from '@/services/sop.service';
import { departmentsService } from '@/services/departments.service';
import { usePermission } from '@/hooks/usePermission';
import { Tabs } from '@/components/ui/tabs';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { formatDate } from '@/lib/dateFormat';

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'mandatory', label: 'Mandatory' },
  { key: 'pending', label: 'Pending' },
  { key: 'completed', label: 'Completed' },
] as const;

type FilterKey = (typeof FILTER_TABS)[number]['key'];
const ALL_DEPT = '__all__';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

export function SopPage() {
  const perms = usePermission();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<FilterKey>('all');
  const [q, setQ] = useState('');
  const [departmentId, setDepartmentId] = useState<string>(ALL_DEPT);
  const [openId, setOpenId] = useState<string | null>(searchParams.get('open'));

  // Deep-link from a notification (?open=<sopId>). openId is already initialized
  // from the param above; here we just strip it from the URL once on mount.
  useEffect(() => {
    if (searchParams.has('open')) {
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params: Partial<ListMySopQuery> = {
    page: 1,
    limit: 100,
    filter,
    q: q || undefined,
    department_id: departmentId === ALL_DEPT ? undefined : departmentId,
  };

  const listQuery = useQuery({
    queryKey: ['sop', 'my-assignments', params],
    queryFn: () => sopService.listMyAssignments(params),
  });

  const deptQuery = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const items = listQuery.data?.data ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Standard Operating Procedures</h1>
        <p className="text-sm text-muted-foreground">View and acknowledge your assigned SOPs</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search SOPs…"
          containerClassName="flex-1 min-w-[200px]"
        />
        <Select value={departmentId} onValueChange={setDepartmentId}>
          <SelectTrigger className="h-10 w-48">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DEPT}>All Departments</SelectItem>
            {(deptQuery.data?.data ?? []).map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs
        tabs={FILTER_TABS.map((t) => ({ key: t.key, label: t.label }))}
        active={filter}
        onChange={(k) => setFilter(k as FilterKey)}
      />

      {listQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl bg-muted/50" />
          ))}
        </div>
      ) : listQuery.isError ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center text-destructive">
          <AlertTriangle className="h-8 w-8 opacity-60" />
          <p className="text-sm font-medium">Failed to load your SOPs</p>
          <Button variant="outline" size="sm" onClick={() => listQuery.refetch()}>Retry</Button>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <FileText className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">No SOPs in this view</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((sop) => (
            <SopCard key={sop.id} sop={sop} onOpen={() => setOpenId(sop.id)} />
          ))}
        </div>
      )}

      {openId && (
        <SopReaderDialog
          sopId={openId}
          canAcknowledge={perms.has(PERMISSIONS.SOP_ACKNOWLEDGE)}
          onClose={() => setOpenId(null)}
          onAcknowledged={() => {
            void queryClient.invalidateQueries({ queryKey: ['sop', 'my-assignments'] });
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
          }}
        />
      )}
    </div>
  );
}

function SopCard({ sop, onOpen }: { sop: MySopItem; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col gap-3 rounded-xl bg-card p-4 text-left shadow-card transition-shadow hover:shadow-card-hover"
    >
      <div className="flex items-start justify-between gap-2">
        <FileText className="h-5 w-5 shrink-0 text-primary" />
        <div className="flex flex-wrap items-center justify-end gap-1">
          {sop.is_mandatory && <Badge variant="warning">Mandatory</Badge>}
          {sop.acknowledged ? (
            <Badge variant="success"><CheckCircle2 className="h-3 w-3" /> Done</Badge>
          ) : sop.overdue ? (
            <Badge variant="destructive"><AlertTriangle className="h-3 w-3" /> Overdue</Badge>
          ) : (
            <Badge variant="secondary"><Clock className="h-3 w-3" /> Pending</Badge>
          )}
        </div>
      </div>
      <div className="min-w-0">
        <p className="truncate font-semibold">{sop.title}</p>
        {sop.summary && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{sop.summary}</p>}
      </div>
      <div className="mt-auto flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{sop.category_name ?? 'Uncategorized'}</span>
        {sop.due_date && <span className={sop.overdue && !sop.acknowledged ? 'text-rose-600 dark:text-rose-400' : ''}>Due {formatDate(sop.due_date)}</span>}
      </div>
    </button>
  );
}

function SopReaderDialog({
  sopId, canAcknowledge, onClose, onAcknowledged,
}: {
  sopId: string;
  canAcknowledge: boolean;
  onClose: () => void;
  onAcknowledged: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const detailQuery = useQuery({
    queryKey: ['sop', 'sop', sopId],
    queryFn: () => sopService.getSop(sopId),
  });

  const acknowledge = useMutation({
    mutationFn: () => sopService.acknowledge(sopId),
    onSuccess: () => {
      toast.success('Marked as read & understood');
      onAcknowledged();
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const sop = detailQuery.data;
  const downloadDoc = async (docId: string, name: string) => {
    try {
      const blob = await sopService.downloadDocument(docId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] w-full max-w-2xl overflow-hidden">
        {detailQuery.isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : !sop ? (
          <div className="py-12 text-center text-sm text-muted-foreground">SOP not found</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{sop.title}</DialogTitle>
              <DialogDescription>
                {sop.sop_code} · {sop.category_name ?? 'Uncategorized'} · v{sop.version}
              </DialogDescription>
            </DialogHeader>

            <div className="-mx-1 max-h-[48vh] overflow-y-auto px-1">
              {sop.summary && <p className="mb-3 text-sm text-muted-foreground">{sop.summary}</p>}
              <RichTextContent html={sop.content} />

              {sop.documents.length > 0 && (
                <div className="mt-4 space-y-1.5 border-t border-border/60 pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attachments</p>
                  {sop.documents.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => downloadDoc(doc.id, doc.original_name)}
                      className="flex w-full items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40"
                    >
                      <Download className="h-4 w-4 text-primary" />
                      <span className="truncate">{doc.original_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {canAcknowledge && (
              <div className="border-t border-border/60 pt-3">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                  I have read and understood this SOP
                </label>
                <Button
                  className="mt-3 w-full"
                  disabled={!confirmed || acknowledge.isPending}
                  onClick={() => acknowledge.mutate()}
                >
                  {acknowledge.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Acknowledge'}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
